/**
 * Strict allow-list sanitizer for announcement banner HTML (`announcements.html_content`).
 *
 * The banner renders admin-authored HTML site-wide via dangerouslySetInnerHTML,
 * and the site CSP still allows `script-src 'unsafe-inline'` — so the CSP is no
 * backstop for inline handlers. Only admins (aal2) can write announcements, but
 * one compromised admin session must not become stored XSS for every visitor.
 * This runs on SAVE (POST/PATCH /api/admin/announcements) and again on RENDER
 * (public banner, /api/announcements/active, admin preview), so a row written
 * any other way (SQL, a future writer) is still neutralised.
 *
 * Why hand-rolled and not DOMPurify: it must run identically during SSR, in the
 * browser and in the Node API routes with no DOM (isomorphic-dompurify drags
 * jsdom into every SSR'd page bundle; plain DOMPurify is a no-op without a
 * DOM). The design keeps the parser simple enough to be safe:
 *
 *   OUTPUT IS RE-SERIALISED, NEVER PASSED THROUGH. The input is tokenised into
 *   text / start-tag / end-tag; only allow-listed tags with allow-listed,
 *   validated attributes are emitted, rebuilt from scratch with double-quoted,
 *   escaped values (an href keeps a bare `&` only where it can't start a
 *   character reference — see escapeAnnouncementHref). All text is emitted with `<` and `>` escaped. So
 *   whatever the browser's parser would have made of a malformed input, the
 *   output contains no markup other than what this function wrote.
 *
 * Everything else is dropped: comments, doctype/processing instructions,
 * unknown tags (their text content is kept), and dangerous containers
 * (script/style/iframe/svg/math/template/…) together with their content.
 * Tags are balanced on output (stray end tags dropped, open tags closed).
 */

/** Tags an announcement may use. Kept to inline/flow formatting + lists + links. */
const ALLOWED_TAGS = new Set([
  'a', 'b', 'br', 'div', 'em', 'h2', 'h3', 'h4', 'hr', 'i', 'li', 'mark',
  'ol', 'p', 's', 'small', 'span', 'strong', 'sub', 'sup', 'u', 'ul',
])
const VOID_TAGS = new Set(['br', 'hr'])

/**
 * Elements dropped WITH their content (raw-text / foreign / embedding
 * containers). Void elements (img, embed, base, link, meta, input, …) have no
 * content and are simply dropped as unknown tags.
 */
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'frameset', 'object', 'applet', 'noscript',
  'noembed', 'noframes', 'template', 'textarea', 'title', 'xmp', 'plaintext',
  'svg', 'math', 'select', 'option', 'head', 'form', 'button',
])

/** Attributes allowed on every allowed tag (values validated below). */
const GLOBAL_ATTRS = new Set(['style', 'title'])
/** Extra attributes per tag. */
const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
}

/**
 * CSS properties an announcement may set inline. Layout/typography only —
 * nothing that can position content over the page (`position`, `z-index`,
 * `inset`, `transform`) or load resources (`background`, `background-image`,
 * `content`, `cursor`, `filter`, `mask`, …).
 */
const ALLOWED_CSS_PROPS = new Set([
  'align-items', 'background-color', 'border-radius', 'color', 'column-gap',
  'display', 'flex', 'flex-direction', 'flex-wrap', 'font-size', 'font-style',
  'font-weight', 'gap', 'justify-content', 'letter-spacing', 'line-height',
  'list-style-type', 'margin', 'margin-bottom', 'margin-left', 'margin-right',
  'margin-top', 'max-width', 'opacity', 'padding', 'padding-bottom',
  'padding-left', 'padding-right', 'padding-top', 'row-gap', 'text-align',
  'text-decoration', 'text-transform', 'vertical-align', 'white-space', 'width',
])
/** CSS functions allowed in a value (colors + arithmetic; no url()/image()/expression()). */
const ALLOWED_CSS_FUNCS = new Set(['rgb', 'rgba', 'hsl', 'hsla', 'calc', 'min', 'max', 'clamp'])
/** A CSS value may only contain these characters (no quotes, `\`, `/`, `:`, `;`, `<`, `@`, `!`). */
const CSS_VALUE_CHARS = /^[#\w\s.,%()+\-*]+$/

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0',
  colon: ':', tab: '\t', newline: '\n', sol: '/', lpar: '(', rpar: ')',
  semi: ';', comma: ',', period: '.', excl: '!', num: '#', equals: '=',
}

/** Decode the entity forms a browser would decode inside an attribute value. */
function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);?/gi, (match, body: string) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10)
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return '\ufffd'
      return String.fromCodePoint(code)
    }
    const named = NAMED_ENTITIES[body.toLowerCase()]
    // Unknown named entity: keep literally. It is re-escaped on output
    // (`&` → `&amp;`), so the browser sees exactly the text we validated.
    return named ?? match
  })
}

function escapeText(value: string): string {
  return value.replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * HTML's legacy named character references — the only names a browser decodes
 * WITHOUT a trailing `;` (WHATWG "named character references" table).
 */
const LEGACY_ENTITY_NAMES = new Set([
  'AElig', 'AMP', 'Aacute', 'Acirc', 'Agrave', 'Aring', 'Atilde', 'Auml', 'COPY', 'Ccedil',
  'ETH', 'Eacute', 'Ecirc', 'Egrave', 'Euml', 'GT', 'Iacute', 'Icirc', 'Igrave', 'Iuml', 'LT',
  'Ntilde', 'Oacute', 'Ocirc', 'Ograve', 'Oslash', 'Otilde', 'Ouml', 'QUOT', 'REG', 'THORN',
  'Uacute', 'Ucirc', 'Ugrave', 'Uuml', 'Yacute', 'aacute', 'acirc', 'acute', 'aelig', 'agrave',
  'amp', 'aring', 'atilde', 'auml', 'brvbar', 'ccedil', 'cedil', 'cent', 'copy', 'curren', 'deg',
  'divide', 'eacute', 'ecirc', 'egrave', 'eth', 'euml', 'frac12', 'frac14', 'frac34', 'gt',
  'iacute', 'icirc', 'iexcl', 'igrave', 'iquest', 'iuml', 'laquo', 'lt', 'macr', 'micro',
  'middot', 'nbsp', 'not', 'ntilde', 'oacute', 'ocirc', 'ograve', 'ordf', 'ordm', 'oslash',
  'otilde', 'ouml', 'para', 'plusmn', 'pound', 'quot', 'raquo', 'reg', 'sect', 'shy', 'sup1',
  'sup2', 'sup3', 'szlig', 'thorn', 'times', 'uacute', 'ucirc', 'ugrave', 'uml', 'uuml',
  'yacute', 'yen', 'yuml',
])

/**
 * Escape an href for a double-quoted attribute, leaving a bare `&` as-is when
 * neither a browser nor `decodeEntities` would read it as a character
 * reference — e.g. the query separators in `?a=1&b=2&utm_source=x`.
 *
 * Why not always `&amp;`: the mobile app reads `html_content` straight from
 * the table and pulls the first href out with a regex that does not decode
 * entities, so a stored `&amp;` breaks its tap-through URL. Leaving the
 * unambiguous ones raw keeps the stored href byte-identical to the URL the
 * admin typed, while the HTML stays spec-equivalent. `&` IS escaped when it
 * could start a reference: `&#…`, `&name;`, a legacy no-semicolon name
 * (`&copy`, `&not`…) or one `decodeEntities` knows (`&amp`, `&lt`…).
 */
export function escapeAnnouncementHref(value: string): string {
  return value
    .replace(/&(?:(#)|([A-Za-z][A-Za-z0-9]*)(;?))?/g, (match, hash?: string, name?: string, semi?: string) => {
      if (hash) return '&amp;#'
      if (!name) return '&'
      const ambiguous =
        semi === ';' || LEGACY_ENTITY_NAMES.has(name) ||
        Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name.toLowerCase())
      return ambiguous ? `&amp;${name}${semi ?? ''}` : match
    })
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Allow only http(s), mailto, site-relative paths and in-page anchors.
 * Parsed with the WHATWG URL parser (what browsers use), which also strips the
 * tab/newline tricks used to hide `java\nscript:`. Protocol-relative `//host`
 * and `/\host` are rejected — they read as internal but leave the site.
 */
export function isSafeAnnouncementHref(raw: string): boolean {
  const value = raw.trim()
  if (!value) return false
  if (/[\u0000-\u001F\u007F]/.test(value.replace(/[\t\n\r]/g, ''))) return false
  const compact = value.replace(/[\t\n\r]/g, '')
  if (/^[/\\]{2}/.test(compact) || /^\/\\/.test(compact)) return false
  let url: URL
  try {
    url = new URL(compact, 'https://sanitizer.invalid/')
  } catch {
    return false
  }
  return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:'
}

/** Keep only allow-listed `prop: value` declarations; returns '' when none survive. */
export function sanitizeInlineStyle(raw: string): string {
  const out: string[] = []
  for (const decl of raw.split(';')) {
    const idx = decl.indexOf(':')
    if (idx <= 0) continue
    const prop = decl.slice(0, idx).trim().toLowerCase()
    const value = decl.slice(idx + 1).trim().replace(/\s+/g, ' ')
    if (!ALLOWED_CSS_PROPS.has(prop) || !value || value.length > 200) continue
    if (!CSS_VALUE_CHARS.test(value)) continue
    const funcs = value.match(/[a-z_-][\w-]*\s*\(/gi) ?? []
    if (funcs.some((f) => !ALLOWED_CSS_FUNCS.has(f.replace(/[\s(]/g, '').toLowerCase()))) continue
    if (/expression|javascript|behavior|binding/i.test(value)) continue
    out.push(`${prop}:${value}`)
  }
  return out.join(';')
}

interface ParsedTag {
  name: string
  attrs: Array<[string, string]>
  end: number // index just past the closing `>`
}

const isNameStart = (c: string | undefined) => !!c && /[a-zA-Z]/.test(c)
const isSpace = (c: string | undefined) => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f'

/** Parse a start tag beginning at `i` (pointing at `<`). Null when unterminated. */
function parseStartTag(html: string, i: number): ParsedTag | null {
  let j = i + 1
  const nameStart = j
  while (j < html.length && !isSpace(html[j]) && html[j] !== '/' && html[j] !== '>') j++
  const name = html.slice(nameStart, j).toLowerCase()
  const attrs: Array<[string, string]> = []
  while (j < html.length) {
    while (j < html.length && (isSpace(html[j]) || html[j] === '/')) j++
    if (j >= html.length) return null
    if (html[j] === '>') return { name, attrs, end: j + 1 }
    const aStart = j
    // An attribute name may start with `=` per the HTML spec; consume it so a
    // malformed `<a =x>` can't stall the loop.
    j++
    while (j < html.length && !isSpace(html[j]) && html[j] !== '/' && html[j] !== '>' && html[j] !== '=') j++
    const aName = html.slice(aStart, j).toLowerCase()
    while (j < html.length && isSpace(html[j])) j++
    let aValue = ''
    if (html[j] === '=') {
      j++
      while (j < html.length && isSpace(html[j])) j++
      const q = html[j]
      if (q === '"' || q === "'") {
        const close = html.indexOf(q, j + 1)
        if (close === -1) return null
        aValue = html.slice(j + 1, close)
        j = close + 1
      } else {
        const vStart = j
        while (j < html.length && !isSpace(html[j]) && html[j] !== '>') j++
        aValue = html.slice(vStart, j)
      }
    }
    attrs.push([aName, aValue])
  }
  return null
}

function serializeStartTag(name: string, attrs: Array<[string, string]>): string {
  const allowed = TAG_ATTRS[name]
  const seen = new Set<string>()
  const out: string[] = []
  let href: string | null = null
  let blank = false
  for (const [rawName, rawValue] of attrs) {
    if (seen.has(rawName)) continue // first occurrence wins, as in browsers
    seen.add(rawName)
    if (!GLOBAL_ATTRS.has(rawName) && !allowed?.has(rawName)) continue
    const value = decodeEntities(rawValue)
    if (rawName === 'style') {
      const style = sanitizeInlineStyle(value)
      if (style) out.push(`style="${escapeAttr(style)}"`)
    } else if (rawName === 'title') {
      out.push(`title="${escapeAttr(value.slice(0, 300))}"`)
    } else if (rawName === 'href') {
      if (isSafeAnnouncementHref(value)) href = value.trim().replace(/[\t\n\r]/g, '')
    } else if (rawName === 'target') {
      blank = value.trim().toLowerCase() === '_blank'
    }
    // `rel` is never copied — it is set below from `target`.
  }
  if (name === 'a') {
    if (href !== null) out.unshift(`href="${escapeAnnouncementHref(href)}"`)
    if (blank) out.push('target="_blank"', 'rel="noopener noreferrer"')
  }
  return `<${name}${out.length ? ` ${out.join(' ')}` : ''}>`
}

/** Sanitize announcement HTML against the allow-list. Idempotent. */
export function sanitizeAnnouncementHtml(input: string | null | undefined): string {
  if (!input) return ''
  const html = String(input)
  let out = ''
  const stack: string[] = []
  let i = 0

  while (i < html.length) {
    const lt = html.indexOf('<', i)
    if (lt === -1) {
      out += escapeText(html.slice(i))
      break
    }
    out += escapeText(html.slice(i, lt))
    i = lt
    const next = html[i + 1]

    // Comment: drop through `-->` (or to end of input if unterminated).
    if (html.startsWith('<!--', i)) {
      const close = html.indexOf('-->', i + 4)
      i = close === -1 ? html.length : close + 3
      continue
    }
    // Doctype / CDATA / processing instruction / bogus comment: drop through `>`.
    if (next === '!' || next === '?') {
      const close = html.indexOf('>', i)
      i = close === -1 ? html.length : close + 1
      continue
    }
    // End tag.
    if (next === '/') {
      const close = html.indexOf('>', i)
      if (close === -1) break // unterminated end tag: drop the rest
      if (isNameStart(html[i + 2])) {
        const name = html.slice(i + 2, close).split(/[\s/]/)[0].toLowerCase()
        const at = stack.lastIndexOf(name)
        if (at !== -1) {
          while (stack.length > at) out += `</${stack.pop()}>`
        }
      }
      i = close + 1
      continue
    }
    // Not a tag opener (`<` followed by a non-letter): literal text.
    if (!isNameStart(next)) {
      out += '&lt;'
      i += 1
      continue
    }

    const tag = parseStartTag(html, i)
    if (!tag) break // unterminated tag: a browser drops it too; drop the rest
    i = tag.end

    if (DROP_WITH_CONTENT.has(tag.name)) {
      // Skip to the matching close tag (case-insensitive); if none, drop the rest.
      const re = new RegExp(`</${tag.name}[\\s/>]`, 'i')
      const rest = html.slice(i)
      const m = re.exec(rest)
      if (!m) {
        i = html.length
      } else {
        const close = html.indexOf('>', i + m.index)
        i = close === -1 ? html.length : close + 1
      }
      continue
    }
    if (!ALLOWED_TAGS.has(tag.name)) continue // unknown tag: drop it, keep its text

    out += serializeStartTag(tag.name, tag.attrs)
    if (!VOID_TAGS.has(tag.name)) stack.push(tag.name)
  }

  while (stack.length) out += `</${stack.pop()}>`
  return out
}
